export type Density = 'compact' | 'normal' | 'comfortable'
export type InventoryDatePreset = 'ALL' | 'TODAY' | 'YESTERDAY' | 'THIS_MONTH' | 'THIS_YEAR' | 'THIS_WEEK' | 'CUSTOM'

export { productCodeDisplay } from '../utils/productCode'

export interface InventoryFilters {
  keyword: string
  statusGroups: string[]
  brandKeys: string[]
  seriesKeys: string[]
  seriesBrandKeys: Record<string, string>
  categoryIds: string[]
  locationScopes: string[]
  locationIds: string[]
  areaIds: string[]
  borrowCustomerIds: string[]
  sourceKinds: string[]
  sourceCustomerIds: string[]
  partnerFunderCustomerIds: string[]
  conditionIds: string[]
  stockedByUserIds: string[]
  collectorUserIds: string[]
  appraiserUserIds: string[]
  includeUnassignedStockedBy: boolean
  includePendingAppraisal: boolean
  costMin: string
  costMax: string
  comprehensiveCostMin: string
  comprehensiveCostMax: string
  salePriceMin: string
  salePriceMax: string
  peerPriceMin: string
  peerPriceMax: string
  stockedPreset: InventoryDatePreset
  stockedFrom: string
  stockedTo: string
  includeMissingStockedAt: boolean
}

export const EMPTY_INVENTORY_FILTERS: InventoryFilters = {
  keyword: '', statusGroups: [], brandKeys: [], seriesKeys: [], seriesBrandKeys: {}, categoryIds: [],
  locationScopes: [], locationIds: [], areaIds: [], borrowCustomerIds: [], sourceKinds: [],
  sourceCustomerIds: [], partnerFunderCustomerIds: [], conditionIds: [], stockedByUserIds: [],
  collectorUserIds: [], appraiserUserIds: [], includeUnassignedStockedBy: false,
  includePendingAppraisal: false, costMin: '', costMax: '',
  comprehensiveCostMin: '', comprehensiveCostMax: '', salePriceMin: '', salePriceMax: '',
  peerPriceMin: '', peerPriceMax: '', stockedPreset: 'ALL', stockedFrom: '', stockedTo: '',
  includeMissingStockedAt: false,
}

export const DATA_COLUMN_KEYS = [
  'cover', 'title', 'brandSeries', 'codes', 'inventoryStatus', 'location', 'sourceType',
  'sourceCustomerName', 'conditionName', 'engraving', 'accessories', 'costAmount',
  'comprehensiveCost', 'partnerFunding', 'salePrice', 'peerPrice', 'stockedByName',
  'stockedAt', 'collectors', 'appraisers',
] as const

export type DataColumnKey = typeof DATA_COLUMN_KEYS[number]
export const COST_COLUMN_KEYS: DataColumnKey[] = ['costAmount', 'comprehensiveCost', 'partnerFunding']
export const DEFAULT_PINNED_COLUMNS: DataColumnKey[] = ['cover', 'title']
export type InventorySortDirection = 'asc' | 'desc'
export interface InventorySortRule { key: DataColumnKey; direction: InventorySortDirection }
export const DEFAULT_INVENTORY_SORTS: readonly InventorySortRule[] = [
  { key: 'stockedAt', direction: 'desc' },
  { key: 'salePrice', direction: 'desc' },
]

export interface TablePreference {
  version: 2
  columns: DataColumnKey[]
  pinned: DataColumnKey[]
  density: Density
}

export function emptyInventoryFilters(): InventoryFilters {
  return structuredClone(EMPTY_INVENTORY_FILTERS)
}

export function createFilterDraft(applied: InventoryFilters) {
  return structuredClone(applied)
}

export function applyFilterDraft(draft: InventoryFilters, canViewCost: boolean) {
  return pruneCostFilters(draft, canViewCost)
}

export function shanghaiDateKey(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now)
}

function formatDate(date: Date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`
}

export function inventoryRangeForPreset(
  preset: InventoryDatePreset,
  today = shanghaiDateKey(),
): [string, string] {
  if (preset === 'ALL' || preset === 'CUSTOM') return ['', '']
  const [year = 0, month = 1, day = 1] = today.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day, 12))
  if (preset === 'TODAY') return [today, today]
  if (preset === 'YESTERDAY') {
    date.setUTCDate(date.getUTCDate() - 1)
    const value = formatDate(date)
    return [value, value]
  }
  if (preset === 'THIS_MONTH') {
    return [`${today.slice(0, 7)}-01`, formatDate(new Date(Date.UTC(year, month, 0, 12)))]
  }
  if (preset === 'THIS_YEAR') return [`${year}-01-01`, `${year}-12-31`]
  const dayOfWeek = date.getUTCDay() || 7
  const start = new Date(date)
  start.setUTCDate(start.getUTCDate() - dayOfWeek + 1)
  const end = new Date(start)
  end.setUTCDate(end.getUTCDate() + 6)
  return [formatDate(start), formatDate(end)]
}

export function setInventoryDatePreset(
  filters: InventoryFilters,
  preset: InventoryDatePreset,
  today?: string,
) {
  const next = structuredClone(filters)
  const [stockedFrom, stockedTo] = inventoryRangeForPreset(preset, today)
  next.stockedPreset = preset
  next.stockedFrom = stockedFrom
  next.stockedTo = stockedTo
  return next
}

export function serializeInventoryFilters(
  filters: InventoryFilters,
  pageNo = 1,
  pageSize = 20,
  sorts?: readonly InventorySortRule[],
) {
  const params = new URLSearchParams({ pageNo: String(pageNo), pageSize: String(pageSize) })
  const appendMany = (key: string, values: string[]) => values.forEach((value) => params.append(key, value))
  const append = (key: string, value: string) => { if (value.trim()) params.set(key, value.trim()) }
  append('keyword', filters.keyword)
  appendMany('statusGroups', filters.statusGroups)
  appendMany('brandKeys', filters.brandKeys)
  appendMany('seriesKeys', filters.seriesKeys)
  appendMany('categoryIds', filters.categoryIds)
  appendMany('locationScopes', filters.locationScopes)
  appendMany('locationIds', filters.locationIds)
  appendMany('areaIds', filters.areaIds)
  appendMany('borrowCustomerIds', filters.borrowCustomerIds)
  appendMany('sourceKinds', filters.sourceKinds)
  appendMany('sourceCustomerIds', filters.sourceCustomerIds)
  appendMany('partnerFunderCustomerIds', filters.partnerFunderCustomerIds)
  appendMany('conditionIds', filters.conditionIds)
  appendMany('stockedByUserIds', filters.stockedByUserIds)
  appendMany('collectorUserIds', filters.collectorUserIds)
  appendMany('appraiserUserIds', filters.appraiserUserIds)
  if (filters.includeUnassignedStockedBy) params.set('includeUnassignedStockedBy', 'true')
  if (filters.includePendingAppraisal) params.set('includePendingAppraisal', 'true')
  append('costMin', filters.costMin)
  append('costMax', filters.costMax)
  append('comprehensiveCostMin', filters.comprehensiveCostMin)
  append('comprehensiveCostMax', filters.comprehensiveCostMax)
  append('salePriceMin', filters.salePriceMin)
  append('salePriceMax', filters.salePriceMax)
  append('peerPriceMin', filters.peerPriceMin)
  append('peerPriceMax', filters.peerPriceMax)
  append('stockedFrom', filters.stockedFrom)
  append('stockedTo', filters.stockedTo)
  if (filters.includeMissingStockedAt) params.set('includeMissingStockedAt', 'true')
  if (sorts !== undefined) {
    params.set('sort', 'LISTED_DESC')
    sorts.forEach((rule) => params.append('sorts', `${rule.key}:${rule.direction}`))
  }
  return params
}

export function createDefaultInventorySorts(): InventorySortRule[] {
  return DEFAULT_INVENTORY_SORTS.map((rule) => ({ ...rule }))
}

export function updateInventorySort(
  current: readonly InventorySortRule[],
  key: DataColumnKey,
  keepExisting: boolean,
): InventorySortRule[] {
  const active = current.find((rule) => rule.key === key)
  const direction: InventorySortDirection | null = !active
    ? 'asc' : active.direction === 'asc' ? 'desc' : null
  if (!keepExisting) return direction ? [{ key, direction }] : []
  const next = current.filter((rule) => rule.key !== key)
  if (direction) next.push({ key, direction })
  return next
}

export function pruneInventorySorts(
  current: InventorySortRule[],
  visibleColumns: readonly DataColumnKey[],
  canViewCost: boolean,
): InventorySortRule[] {
  const visible = new Set(visibleColumns)
  const next = current.filter((rule) => visible.has(rule.key)
    && (canViewCost || !COST_COLUMN_KEYS.includes(rule.key)))
  return next.length === current.length && next.every((rule, index) => rule === current[index])
    ? current : next
}

export function inventorySortSummary(
  sorts: readonly InventorySortRule[],
  labels: Readonly<Record<DataColumnKey, string>>,
) {
  return sorts.length
    ? sorts.map((rule) => `${labels[rule.key]} ${rule.direction === 'asc' ? '↑' : '↓'}`).join(' · ')
    : '未排序'
}

export function clearFilterGroup(filters: InventoryFilters, keys: Array<keyof InventoryFilters>) {
  const next = structuredClone(filters)
  for (const key of keys) {
    if (key === 'stockedPreset') {
      next.stockedPreset = 'ALL'
      continue
    }
    const value = next[key]
    ;(next as unknown as Record<string, unknown>)[key] = Array.isArray(value)
      ? [] : typeof value === 'boolean' ? false : value && typeof value === 'object' ? {} : ''
  }
  return next
}

export function toggleWholeLocation(
  filters: InventoryFilters,
  locationId: string,
  locationAreaIds: string[],
) {
  const selected = filters.locationIds.includes(locationId)
  const areaSet = new Set(locationAreaIds)
  return {
    ...structuredClone(filters),
    locationIds: selected
      ? filters.locationIds.filter((id) => id !== locationId)
      : [...filters.locationIds.filter((id) => id !== locationId), locationId],
    areaIds: filters.areaIds.filter((id) => !areaSet.has(id)),
  }
}

export function toggleLocationArea(
  filters: InventoryFilters,
  locationId: string,
  areaId: string,
  locationAreaIds: string[],
) {
  const wholeSelected = filters.locationIds.includes(locationId)
  const areaSet = new Set(locationAreaIds)
  const outsideAreaIds = filters.areaIds.filter((id) => !areaSet.has(id))
  const currentlySelected = wholeSelected
    ? locationAreaIds
    : filters.areaIds.filter((id) => areaSet.has(id))
  const selectedAreaIds = currentlySelected.includes(areaId)
    ? currentlySelected.filter((id) => id !== areaId)
    : [...currentlySelected, areaId]
  const normalizeToWhole = locationAreaIds.length > 0
    && locationAreaIds.every((id) => selectedAreaIds.includes(id))
  return {
    ...structuredClone(filters),
    locationIds: normalizeToWhole
      ? [...filters.locationIds.filter((id) => id !== locationId), locationId]
      : filters.locationIds.filter((id) => id !== locationId),
    areaIds: normalizeToWhole ? outsideAreaIds : [...outsideAreaIds, ...selectedAreaIds],
  }
}

export function pruneCostFilters(filters: InventoryFilters, canViewCost: boolean) {
  if (canViewCost) return structuredClone(filters)
  return clearFilterGroup(filters, ['costMin', 'costMax', 'comprehensiveCostMin', 'comprehensiveCostMax'])
}

export function normalizeTablePreference(value: unknown, canViewCost: boolean): TablePreference {
  const record = value && typeof value === 'object' ? value as Record<string, unknown> : {}
  const allowed = new Set<DataColumnKey>(DATA_COLUMN_KEYS.filter((key) => canViewCost || !COST_COLUMN_KEYS.includes(key)))
  const migrateColumn = (key: unknown) => key === 'operatorName'
    ? 'stockedByName' : key === 'intakeTime' ? 'stockedAt' : key
  const sourceColumns = Array.isArray(record.columns) ? record.columns.map(migrateColumn) : DATA_COLUMN_KEYS
  const columns = sourceColumns.filter((key): key is DataColumnKey => typeof key === 'string' && allowed.has(key as DataColumnKey))
  const uniqueColumns = [...new Set(columns)]
  for (const key of allowed) if (!uniqueColumns.includes(key) && !Array.isArray(record.columns)) uniqueColumns.push(key)
  const visible = uniqueColumns.length ? uniqueColumns : [...allowed]
  const sourcePinned = Array.isArray(record.pinned) ? record.pinned.map(migrateColumn) : DEFAULT_PINNED_COLUMNS
  const pinned = [...new Set(sourcePinned.filter((key): key is DataColumnKey =>
    typeof key === 'string' && visible.includes(key as DataColumnKey)))].slice(0, 2)
  const density = record.density === 'normal' || record.density === 'comfortable' ? record.density : 'compact'
  return { version: 2, columns: visible, pinned, density }
}

export function orderVisibleColumns(columns: DataColumnKey[], pinned: DataColumnKey[]) {
  return [...pinned.filter((key) => columns.includes(key)), ...columns.filter((key) => !pinned.includes(key))]
}

export function moveColumn(columns: DataColumnKey[], key: DataColumnKey, direction: -1 | 1) {
  const index = columns.indexOf(key)
  const target = index + direction
  if (index < 0 || target < 0 || target >= columns.length) return columns
  const next = [...columns]
  ;[next[index], next[target]] = [next[target]!, next[index]!]
  return next
}

export function partnerFundingDisplay(
  storeContributionAmount?: string,
  contributions: Record<string, unknown>[] = [],
) {
  const scalarText = (value: unknown) => typeof value === 'string' || typeof value === 'number'
    ? String(value).trim() : ''
  const result: Array<{ name: string; amount: string }> = []
  const storeAmount = storeContributionAmount?.trim()
  if (storeAmount) result.push({ name: '我方', amount: storeAmount })
  for (const contribution of contributions) {
    const amount = scalarText(contribution.amount)
    if (!amount) continue
    const rawName = contribution.name ?? contribution.customerName
    const name = scalarText(rawName) || '合伙人'
    result.push({ name, amount })
  }
  return result
}
